import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  DollarSign,
  Clock,
  StickyNote,
  CheckCircle2,
  Star,
  ExternalLink,
  Save,
  Pencil,
  Repeat,
} from "lucide-react";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useT } from "../context/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";
import { addToDeviceCalendar } from "../lib/calendarExport";
import { resolveEventDurationMinutes } from "../lib/duration";

// ---------- Date utilities ----------
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const startOfWeek = (d) => {
  // Sunday-start to match US conventions
  const x = startOfDay(d);
  x.setDate(x.getDate() - x.getDay());
  return x;
};
const startOfMonth = (d) => {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
};
const endOfMonth = (d) => {
  const x = startOfMonth(d);
  x.setMonth(x.getMonth() + 1);
  return x;
};
const sameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

// Resolve the calendar day for an event, honoring `scheduled_time_set`:
//   • timeSet=true  → use the browser's local Date (a 6 PM event in EDT
//     should appear on the local-time day, even if that's a different
//     UTC day).
//   • timeSet=false → use the UTC date of the stored ISO. Date-only
//     events are anchored at 12:00 UTC by the backend so the UTC date IS
//     the intended calendar date for any normal timezone. This prevents
//     "June 8 UTC midnight" from appearing as "June 7" in a UTC-negative
//     local timezone (the original bug).
const eventDay = (event) => {
  if (!event?.scheduled_at) return null;
  const dt = new Date(event.scheduled_at);
  if (isNaN(dt.getTime())) return null;
  if (event.scheduled_time_set) {
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  }
  return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
};

const fmtTime = (date, locale) =>
  date.toLocaleTimeString(locale === "es" ? "es-ES" : "en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

// Returns `"6:00 PM"` or null. Null means "Time not set" — caller renders
// the localized "Time not set" pill instead.
const fmtEventTime = (event, locale) => {
  if (!event?.scheduled_at || !event.scheduled_time_set) return null;
  const dt = new Date(event.scheduled_at);
  if (isNaN(dt.getTime())) return null;
  return fmtTime(dt, locale);
};

// Stable key for conflict detection. Events sharing this key happen at
// the same moment.
const eventConflictKey = (event) => {
  if (!event?.scheduled_at) return null;
  if (!event.scheduled_time_set) return null;
  return event.scheduled_at;
};

const fmtDate = (date, locale, opts = { month: "long", day: "numeric", year: "numeric" }) =>
  date.toLocaleDateString(locale === "es" ? "es-ES" : "en-US", opts);

const fmtWeekday = (date, locale) =>
  date.toLocaleDateString(locale === "es" ? "es-ES" : "en-US", { weekday: "short" });

// ---------- Status pill ----------
const STATUS_STYLES = {
  booked: "bg-green-100 text-green-800 border-green-200",
  completed: "bg-sky-100 text-sky-800 border-sky-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

const StatusPill = ({ event, t }) => {
  const ds = event.display_status || event.status;
  const label =
    ds === "completed"
      ? t("cal.statusCompleted")
      : ds === "cancelled"
        ? t("cal.statusCancelled")
        : t("cal.statusBooked");
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${STATUS_STYLES[ds] || STATUS_STYLES.booked}`}
      data-testid={`cal-status-${ds}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
      {label}
    </span>
  );
};

// ---------- Event card (compact, used in lists/week/today) ----------
const EventCard = ({ event, onOpen, t, locale, compact = false, conflict = false }) => {
  const timeStr = fmtEventTime(event, locale);
  return (
    <button
      type="button"
      onClick={() => onOpen(event)}
      className="w-full text-left rounded-xl border border-gray-200 bg-white hover:border-green-300 hover:shadow-sm transition px-3 py-2.5 group focus:outline-none focus:ring-2 focus:ring-green-200"
      data-testid={`cal-event-card-${event.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <StatusPill event={event} t={t} />
            {timeStr ? (
              <span className="text-[11px] font-medium text-gray-500" data-testid={`cal-event-time-${event.id}`}>
                {timeStr}
              </span>
            ) : (
              <span
                className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5"
                data-testid={`cal-event-time-not-set-${event.id}`}
              >
                {t("cal.timeNotSet")}
              </span>
            )}
            {conflict && (
              <span
                className="text-[10px] font-bold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 rounded-full px-1.5 py-0.5"
                data-testid={`cal-event-conflict-${event.id}`}
                title={t("cal.conflictHelper")}
              >
                {t("cal.conflict")}
              </span>
            )}
          </div>
          <p className="font-semibold text-gray-900 text-sm truncate" data-testid={`cal-event-name-${event.id}`}>
            {event.customer_name || "Customer"}
          </p>
          {!compact && (event.service_type || event.address) && (
            <p className="text-xs text-gray-600 truncate mt-0.5">
              {event.service_type || ""}
              {event.service_type && event.address ? " · " : ""}
              {event.address || ""}
            </p>
          )}
          {typeof event.estimated_job_value === "number" && event.estimated_job_value > 0 && (
            <p className="text-xs font-semibold text-green-800 mt-0.5">
              ${Number(event.estimated_job_value).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

// ---------- Month cell event chip ----------
const MonthEventChip = ({ event, onOpen, conflict = false }) => {
  const ds = event.display_status || event.status;
  const dotColor = ds === "completed" ? "bg-sky-500" : ds === "cancelled" ? "bg-red-500" : "bg-green-600";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen(event);
      }}
      className={`w-full text-left px-1.5 py-0.5 rounded-md text-[11px] font-medium hover:bg-green-50 transition flex items-center gap-1 truncate ${conflict ? "ring-1 ring-red-200" : ""}`}
      data-testid={`cal-month-chip-${event.id}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`} />
      <span className="truncate">{event.customer_name || "Customer"}</span>
    </button>
  );
};

// ---------- Event Details Panel ----------
const EventDetailsDialog = ({ event, open, onClose, onUpdated, t, locale }) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dateInput, setDateInput] = useState("");
  const [timeInput, setTimeInput] = useState("");
  const [durInput, setDurInput] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!event) return;
    if (event.scheduled_at) {
      const dt = new Date(event.scheduled_at);
      const pad = (n) => String(n).padStart(2, "0");
      if (event.scheduled_time_set) {
        // Real clock time → show in browser local timezone.
        setDateInput(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`);
        setTimeInput(`${pad(dt.getHours())}:${pad(dt.getMinutes())}`);
      } else {
        // Date-only event (anchored at 12:00 UTC) → seed the date input
        // from the UTC date so it matches what the customer requested,
        // and leave time blank so the owner explicitly picks one.
        setDateInput(`${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`);
        setTimeInput("");
      }
    } else {
      setDateInput("");
      setTimeInput("");
    }
    setDurInput(String(resolveEventDurationMinutes(event)));
    setNotes(event.calendar_notes || "");
    setEditing(false);
  }, [event]);

  if (!event) return null;
  const timeStr = fmtEventTime(event, locale);
  const dayForDisplay = eventDay(event);

  const saveSchedule = async () => {
    if (!dateInput) {
      toast.error(t("cal.savedScheduleErr"));
      return;
    }
    setSaving(true);
    try {
      const [y, mo, d] = dateInput.split("-").map((n) => parseInt(n, 10));
      const hasExplicitTime = !!timeInput;
      let iso;
      if (hasExplicitTime) {
        // Owner picked an explicit time → build a NAIVE local ISO
        // ("YYYY-MM-DDTHH:MM:00") with NO timezone marker. This is the
        // business's local appointment time; the backend stores it
        // verbatim and `new Date(iso)` in every browser parses it as
        // local time → renders the literal 9:45 AM the owner picked,
        // not 5:45 AM after a UTC-to-local conversion.
        const [hh = 0, mm = 0] = timeInput.split(":").map((n) => parseInt(n, 10));
        const pad = (n) => String(n).padStart(2, "0");
        iso = `${y}-${pad(mo)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00`;
      } else {
        // No time picked → anchor at noon UTC of the chosen date so the
        // event displays on the correct calendar day across timezones.
        // Send an explicit scheduled_time_set=false so the backend
        // doesn't auto-flip it to true.
        const noonUtc = new Date(Date.UTC(y, (mo || 1) - 1, d || 1, 12, 0, 0));
        iso = noonUtc.toISOString();
      }
      const payload = {
        scheduled_at: iso,
        scheduled_time_set: hasExplicitTime,
        duration_minutes: Math.max(15, Math.min(24 * 60, parseInt(durInput, 10) || resolveEventDurationMinutes(event))),
        calendar_notes: notes || null,
      };
      await api.patch(`/leads/${event.lead_id}`, payload);
      toast.success(t("cal.savedSchedule"));
      setEditing(false);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(t("cal.savedScheduleErr"));
    } finally {
      setSaving(false);
    }
  };

  const markCompleted = async () => {
    try {
      await api.patch(`/leads/${event.lead_id}`, { status: "closed" });
      toast.success(t("cal.markCompleted") + " ✓");
      onUpdated && onUpdated();
    } catch {
      toast.error(t("cal.savedScheduleErr"));
    }
  };

  const requestReview = async () => {
    try {
      await api.post(`/leads/${event.lead_id}/request-review`);
      toast.success(t("cal.requestReview") + " ✓");
      onUpdated && onUpdated();
    } catch (err) {
      const msg = err?.response?.data?.detail || t("cal.savedScheduleErr");
      toast.error(msg);
    }
  };

  const addToDevice = () => {
    // Only send a real time when the event actually carries one. Pure
    // date-only events open at 9 AM local on the chosen day so the
    // owner's external calendar gets a reasonable default.
    let start;
    if (event.scheduled_time_set && event.scheduled_at) {
      start = new Date(event.scheduled_at);
    } else if (dayForDisplay) {
      start = new Date(dayForDisplay.getFullYear(), dayForDisplay.getMonth(), dayForDisplay.getDate(), 9, 0, 0);
    } else {
      start = new Date();
    }
    addToDeviceCalendar({
      title: `${event.service_type || "Cleaning"} — ${event.customer_name || "Customer"}`,
      start,
      durationMinutes: resolveEventDurationMinutes(event),
      description: [
        event.phone ? `Phone: ${event.phone}` : null,
        event.email ? `Email: ${event.email}` : null,
        event.address ? `Address: ${event.address}` : null,
        event.calendar_notes ? `Notes: ${event.calendar_notes}` : null,
      ]
        .filter(Boolean)
        .join("\n"),
      location: event.address || "",
      uid: event.id,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="cal-event-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-green-800" />
            {t("cal.eventDetails")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <StatusPill event={event} t={t} />
            {dayForDisplay && (
              <span className="text-xs text-gray-600" data-testid="cal-dialog-when">
                {fmtDate(dayForDisplay, locale, { weekday: "short", month: "short", day: "numeric" })}
                {timeStr ? `, ${timeStr}` : ` · ${t("cal.timeNotSet")}`}
              </span>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 p-3 space-y-2">
            <p className="text-base font-semibold text-gray-900" data-testid="cal-dialog-name">
              {event.customer_name || "Customer"}
            </p>
            {event.service_type && (
              <p className="text-sm text-gray-700">{event.service_type}</p>
            )}
            <div className="space-y-1 pt-1">
              {event.phone && (
                <p className="text-xs text-gray-700 flex items-center gap-1.5">
                  <Phone className="w-3 h-3 text-gray-400" /> {event.phone}
                </p>
              )}
              {event.email && (
                <p className="text-xs text-gray-700 flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-gray-400" /> {event.email}
                </p>
              )}
              {event.address && (
                <p className="text-xs text-gray-700 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-gray-400" /> {event.address}
                </p>
              )}
              {event.frequency && event.frequency !== "one_time" ? (
                <p
                  className="text-xs text-gray-700 flex items-center gap-1.5"
                  data-testid="cal-dialog-frequency"
                >
                  <Repeat className="w-3 h-3 text-gray-400" />
                  {t("cal.frequencyLabel")}:{" "}
                  <span className="font-medium text-gray-900">
                    {t(`lead.fieldFrequencyValue.${event.frequency}`)}
                  </span>
                </p>
              ) : null}
              {typeof event.estimated_job_value === "number" && event.estimated_job_value > 0 && (
                <p className="text-xs text-gray-700 flex items-center gap-1.5">
                  <DollarSign className="w-3 h-3 text-gray-400" /> ${Number(event.estimated_job_value).toLocaleString()} · {t("cal.jobValue").toLowerCase()}
                </p>
              )}
              <p className="text-xs text-gray-700 flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-gray-400" /> {resolveEventDurationMinutes(event)} {t("todayJobs.minutes")}
              </p>
            </div>
          </div>

          {/* Schedule editor */}
          {!editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="w-full text-left rounded-xl border border-dashed border-gray-300 hover:border-green-400 hover:bg-green-50 transition px-3 py-2 text-sm font-semibold text-gray-700 flex items-center justify-between"
              data-testid="cal-edit-schedule-button"
            >
              <span className="flex items-center gap-2">
                <Pencil className="w-3.5 h-3.5" />
                {t("cal.editSchedule")}
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          ) : (
            <div className="rounded-xl border border-gray-200 p-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{t("cal.dateLabel")}</Label>
                  <Input
                    type="date"
                    value={dateInput}
                    onChange={(e) => setDateInput(e.target.value)}
                    data-testid="cal-schedule-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("cal.timeLabel")}</Label>
                  <Input
                    type="time"
                    value={timeInput}
                    onChange={(e) => setTimeInput(e.target.value)}
                    data-testid="cal-schedule-time"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t("cal.durationLabel")}</Label>
                <Input
                  type="number"
                  min="15"
                  step="15"
                  value={durInput}
                  onChange={(e) => setDurInput(e.target.value)}
                  data-testid="cal-schedule-duration"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  <StickyNote className="w-3 h-3 text-gray-400" />
                  {t("cal.calendarNotes")}
                </Label>
                <Textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("cal.calendarNotesPlaceholder")}
                  data-testid="cal-schedule-notes"
                />
              </div>
              <p className="text-[11px] text-gray-500">{t("cal.scheduledHelper")}</p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)} data-testid="cal-schedule-cancel">
                  {t("cal.cancel")}
                </Button>
                <Button
                  size="sm"
                  className="bg-green-800 hover:bg-green-900 text-white"
                  onClick={saveSchedule}
                  disabled={saving}
                  data-testid="cal-schedule-save"
                >
                  <Save className="w-3.5 h-3.5 mr-1" />
                  {t("cal.save")}
                </Button>
              </div>
            </div>
          )}

          {event.calendar_notes && !editing && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
              <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5 mb-1">
                <StickyNote className="w-3 h-3" />
                {t("cal.notes")}
              </p>
              <p className="text-xs text-amber-900 whitespace-pre-wrap">{event.calendar_notes}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/leads/${event.lead_id}`)}
              data-testid="cal-action-view-lead"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1" />
              {t("cal.viewLead")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={addToDevice}
              data-testid="cal-action-add-to-device"
            >
              <CalendarIcon className="w-3.5 h-3.5 mr-1" />
              {t("cal.addToCalendar")}
            </Button>
            {event.status !== "closed" && (
              <Button
                variant="outline"
                size="sm"
                onClick={markCompleted}
                data-testid="cal-action-mark-completed"
                className="text-sky-800 border-sky-200 hover:bg-sky-50"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                {t("cal.markCompleted")}
              </Button>
            )}
            {!event.review_request_sent_at && (
              <Button
                variant="outline"
                size="sm"
                onClick={requestReview}
                data-testid="cal-action-request-review"
                className="text-amber-800 border-amber-200 hover:bg-amber-50"
              >
                <Star className="w-3.5 h-3.5 mr-1" />
                {t("cal.requestReview")}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Conflict map helper ----------
// Build a Set of conflict keys ("scheduled_at" iso) shared by 2+ events.
// Only events with an explicit clock time count for conflicts — "Time
// not set" events on the same day are NOT flagged.
const computeConflicts = (events) => {
  const counts = new Map();
  events.forEach((e) => {
    const k = eventConflictKey(e);
    if (!k) return;
    counts.set(k, (counts.get(k) || 0) + 1);
  });
  return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([k]) => k));
};

// ---------- Today view ----------
const TodayView = ({ events, onOpen, t, locale }) => {
  const today = startOfDay(new Date());
  const todays = events.filter((e) => {
    const d = eventDay(e);
    return d && sameDay(d, today);
  });
  const conflicts = useMemo(() => computeConflicts(todays), [todays]);
  // Sort: timed events first by clock, then untimed at the bottom.
  const sorted = useMemo(() => {
    const timed = todays.filter((e) => e.scheduled_time_set).sort(
      (a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at),
    );
    const untimed = todays.filter((e) => !e.scheduled_time_set);
    return [...timed, ...untimed];
  }, [todays]);
  if (todays.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center" data-testid="cal-today-empty">
        <p className="text-sm font-semibold text-gray-900">{t("cal.noJobsToday")}</p>
        <p className="text-xs text-gray-500 mt-1">{t("cal.emptyBody")}</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="cal-today-list">
      {sorted.map((e) => (
        <EventCard
          key={e.id}
          event={e}
          onOpen={onOpen}
          t={t}
          locale={locale}
          conflict={conflicts.has(eventConflictKey(e))}
        />
      ))}
    </div>
  );
};

// ---------- Week view ----------
const WeekView = ({ events, onOpen, t, locale, anchorDate, onAnchorChange }) => {
  const weekStart = startOfWeek(anchorDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const eventsByDay = useMemo(() => {
    const buckets = days.map(() => []);
    events.forEach((e) => {
      const d = eventDay(e);
      if (!d) return;
      const idx = days.findIndex((day) => sameDay(day, d));
      if (idx >= 0) buckets[idx].push(e);
    });
    // Sort each day: timed events first by clock, then untimed.
    buckets.forEach((b) =>
      b.sort((a, b2) => {
        if (a.scheduled_time_set !== b2.scheduled_time_set) {
          return a.scheduled_time_set ? -1 : 1;
        }
        return new Date(a.scheduled_at) - new Date(b2.scheduled_at);
      }),
    );
    return buckets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, anchorDate]);

  const conflicts = useMemo(() => computeConflicts(events), [events]);
  const today = startOfDay(new Date());

  return (
    <div data-testid="cal-week-view">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-gray-700" data-testid="cal-week-range">
          {fmtDate(weekStart, locale, { month: "short", day: "numeric" })} —{" "}
          {fmtDate(addDays(weekStart, 6), locale, { month: "short", day: "numeric", year: "numeric" })}
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => onAnchorChange(addDays(anchorDate, -7))} data-testid="cal-week-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onAnchorChange(new Date())} data-testid="cal-week-today">
            {t("cal.todayLabel")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onAnchorChange(addDays(anchorDate, 7))} data-testid="cal-week-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="hidden md:grid grid-cols-7 gap-2">
        {days.map((d, i) => (
          <div
            key={i}
            className={`rounded-xl border ${sameDay(d, today) ? "border-green-300 bg-green-50/30" : "border-gray-200 bg-white"} p-2 min-h-[160px] flex flex-col`}
            data-testid={`cal-week-day-${i}`}
          >
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-[11px] uppercase font-semibold text-gray-500">
                {fmtWeekday(d, locale)}
              </span>
              <span className={`text-sm font-bold ${sameDay(d, today) ? "text-green-800" : "text-gray-900"}`}>
                {d.getDate()}
              </span>
            </div>
            <div className="space-y-1.5 flex-1 overflow-y-auto">
              {eventsByDay[i].length === 0 ? (
                <p className="text-[11px] text-gray-400 italic">—</p>
              ) : (
                eventsByDay[i].map((e) => (
                  <EventCard
                    key={e.id}
                    event={e}
                    onOpen={onOpen}
                    t={t}
                    locale={locale}
                    compact
                    conflict={conflicts.has(eventConflictKey(e))}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile: agenda list across the week */}
      <div className="md:hidden space-y-3">
        {days.map((d, i) => {
          const dayEvents = eventsByDay[i];
          if (dayEvents.length === 0 && !sameDay(d, today)) return null;
          return (
            <div key={i} data-testid={`cal-week-mobile-day-${i}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-xs font-bold uppercase ${sameDay(d, today) ? "text-green-800" : "text-gray-500"}`}>
                  {sameDay(d, today) ? t("cal.todayLabel") : fmtWeekday(d, locale)}
                </span>
                <span className="text-xs text-gray-500">
                  {fmtDate(d, locale, { month: "short", day: "numeric" })}
                </span>
              </div>
              {dayEvents.length === 0 ? (
                <div className="rounded-xl bg-white border border-dashed border-gray-200 p-3 text-xs text-gray-400 italic">
                  —
                </div>
              ) : (
                <div className="space-y-2">
                  {dayEvents.map((e) => (
                    <EventCard
                      key={e.id}
                      event={e}
                      onOpen={onOpen}
                      t={t}
                      locale={locale}
                      conflict={conflicts.has(eventConflictKey(e))}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Month view ----------
const MonthView = ({ events, onOpen, t, locale, anchorDate, onAnchorChange }) => {
  const first = startOfMonth(anchorDate);
  const last = endOfMonth(anchorDate);
  const gridStart = startOfWeek(first);
  // Total weeks needed to cover from gridStart through end-of-month.
  const lastIdx = Math.ceil((last - gridStart) / (24 * 60 * 60 * 1000));
  const weeks = Math.max(5, Math.ceil(lastIdx / 7));
  const used = Array.from({ length: weeks * 7 }, (_, i) => addDays(gridStart, i));

  const eventsByKey = useMemo(() => {
    const m = new Map();
    events.forEach((e) => {
      const d = eventDay(e);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(e);
    });
    m.forEach((arr) =>
      arr.sort((a, b) => {
        if (a.scheduled_time_set !== b.scheduled_time_set) {
          return a.scheduled_time_set ? -1 : 1;
        }
        return new Date(a.scheduled_at) - new Date(b.scheduled_at);
      }),
    );
    return m;
  }, [events]);

  const conflicts = useMemo(() => computeConflicts(events), [events]);
  const today = startOfDay(new Date());
  const weekdayLabels = useMemo(() => {
    const start = startOfWeek(new Date());
    return Array.from({ length: 7 }, (_, i) => fmtWeekday(addDays(start, i), locale));
  }, [locale]);

  return (
    <div data-testid="cal-month-view">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-gray-700" data-testid="cal-month-label">
          {fmtDate(first, locale, { month: "long", year: "numeric" })}
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const x = new Date(anchorDate);
              x.setMonth(x.getMonth() - 1);
              onAnchorChange(x);
            }}
            data-testid="cal-month-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onAnchorChange(new Date())} data-testid="cal-month-today">
            {t("cal.todayLabel")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const x = new Date(anchorDate);
              x.setMonth(x.getMonth() + 1);
              onAnchorChange(x);
            }}
            data-testid="cal-month-next"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Desktop: full grid */}
      <div className="hidden md:block">
        <div className="grid grid-cols-7 mb-1">
          {weekdayLabels.map((w, i) => (
            <div key={i} className="text-[11px] uppercase font-semibold text-gray-500 px-1.5">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {used.map((d, i) => {
            const inMonth = d.getMonth() === first.getMonth();
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            const cellEvents = eventsByKey.get(key) || [];
            const isToday = sameDay(d, today);
            return (
              <div
                key={i}
                className={`rounded-lg border p-1.5 min-h-[88px] ${
                  isToday
                    ? "border-green-300 bg-green-50/30"
                    : inMonth
                      ? "border-gray-200 bg-white"
                      : "border-gray-100 bg-gray-50/50"
                }`}
                data-testid={`cal-month-cell-${i}`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span
                    className={`text-xs font-semibold ${
                      isToday ? "text-green-800" : inMonth ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {d.getDate()}
                  </span>
                  {cellEvents.length > 0 && (
                    <span className="text-[10px] font-bold text-green-800 bg-green-100 px-1 rounded">
                      {cellEvents.length}
                    </span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {cellEvents.slice(0, 3).map((e) => (
                    <MonthEventChip
                      key={e.id}
                      event={e}
                      onOpen={onOpen}
                      conflict={conflicts.has(eventConflictKey(e))}
                    />
                  ))}
                  {cellEvents.length > 3 && (
                    <p className="text-[10px] text-gray-500 px-1.5">+{cellEvents.length - 3}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: agenda list for the visible month */}
      <div className="md:hidden space-y-2">
        {Array.from(eventsByKey.entries())
          .filter(([k]) => {
            const [y, m] = k.split("-").map((n) => parseInt(n, 10));
            return y === first.getFullYear() && m === first.getMonth();
          })
          .sort(([a], [b]) => {
            const da = a.split("-").map((n) => parseInt(n, 10));
            const dbb = b.split("-").map((n) => parseInt(n, 10));
            return new Date(da[0], da[1], da[2]) - new Date(dbb[0], dbb[1], dbb[2]);
          })
          .map(([k, list]) => {
            const [y, m, d] = k.split("-").map((n) => parseInt(n, 10));
            const dateObj = new Date(y, m, d);
            return (
              <div key={k}>
                <div className="flex items-center gap-2 mb-1.5 mt-2">
                  <span className={`text-xs font-bold uppercase ${sameDay(dateObj, today) ? "text-green-800" : "text-gray-500"}`}>
                    {sameDay(dateObj, today) ? t("cal.todayLabel") : fmtWeekday(dateObj, locale)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {fmtDate(dateObj, locale, { month: "short", day: "numeric" })}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {list.map((e) => (
                    <EventCard
                      key={e.id}
                      event={e}
                      onOpen={onOpen}
                      t={t}
                      locale={locale}
                      conflict={conflicts.has(eventConflictKey(e))}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        {Array.from(eventsByKey.entries()).filter(([k]) => {
          const [y, m] = k.split("-").map((n) => parseInt(n, 10));
          return y === first.getFullYear() && m === first.getMonth();
        }).length === 0 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
            <p className="text-sm font-semibold text-gray-900">{t("cal.noJobsThisMonth")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Main page ----------
export default function CalendarPage() {
  const { t, lang } = useT();
  const [view, setView] = useState(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("tasklio_cal_view") : null;
    return stored && ["today", "week", "month"].includes(stored) ? stored : "week";
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [selected, setSelected] = useState(null);
  // When the user lands here from a Lead's "View in Calendar" CTA we get a
  // `?lead=<lead_id>` query param. Once the events load we (a) snap the
  // calendar anchor to that appointment's day so it's visible in the
  // current view and (b) open the detail dialog for it.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusLeadId = searchParams.get("lead");

  const persistView = (v) => {
    setView(v);
    try {
      window.localStorage.setItem("tasklio_cal_view", v);
    } catch {
      // ignore
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/calendar/events");
      setEvents(res.data?.events || []);
    } catch (err) {
      toast.error("Couldn't load calendar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-focus the lead-event when arriving from a "View in Calendar" CTA.
  // Runs whenever events load or the query param changes. Clears the
  // param after applying so a refresh doesn't keep re-opening the dialog.
  useEffect(() => {
    if (!focusLeadId || loading || !events.length) return;
    const match = events.find(
      (e) => e.lead_id === focusLeadId || e.id === focusLeadId
    );
    if (!match) return;
    setSelected(match);
    if (match.scheduled_at) {
      try {
        const dt = new Date(match.scheduled_at);
        if (!Number.isNaN(dt.getTime())) {
          setAnchorDate(dt);
          // Force "today" view if the event is today so the agenda shows it.
          // Otherwise leave the user's preferred view as-is.
        }
      } catch {
        // ignore — anchor stays at default
      }
    }
    // Strip the query param so reload doesn't re-fire.
    const next = new URLSearchParams(searchParams);
    next.delete("lead");
    setSearchParams(next, { replace: true });
  }, [focusLeadId, loading, events, searchParams, setSearchParams]);

  const onUpdated = async () => {
    await load();
    // Refresh selected event with latest data
    if (selected) {
      const fresh = (await api.get("/calendar/events")).data?.events || [];
      const m = fresh.find((e) => e.id === selected.id);
      setSelected(m || null);
      setEvents(fresh);
    }
  };

  const totalCount = events.length;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <header className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-9 h-9 rounded-xl bg-green-100 text-green-800 flex items-center justify-center">
              <CalendarIcon className="w-4 h-4" />
            </span>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-testid="cal-title">
              {t("cal.title")}
            </h1>
          </div>
          <p className="text-sm text-gray-600 max-w-2xl">{t("cal.subtitle")}</p>
        </header>

        {/* View switcher */}
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 shadow-sm" data-testid="cal-view-switcher">
            {[
              { v: "today", label: t("cal.viewToday") },
              { v: "week", label: t("cal.viewWeek") },
              { v: "month", label: t("cal.viewMonth") },
            ].map(({ v, label }) => (
              <button
                key={v}
                type="button"
                onClick={() => persistView(v)}
                className={`px-3 sm:px-4 py-1.5 text-sm font-semibold rounded-lg transition ${
                  view === v ? "bg-green-800 text-white shadow-sm" : "text-gray-700 hover:bg-gray-50"
                }`}
                data-testid={`cal-view-${v}`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 hidden sm:block">
            {totalCount === 1 ? t("cal.oneJobScheduled") : t("cal.jobsScheduled", totalCount)}
          </p>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500" data-testid="cal-loading">
            …
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center" data-testid="cal-empty-state">
            <span className="inline-flex w-12 h-12 rounded-2xl bg-green-50 text-green-800 items-center justify-center mb-3">
              <CalendarIcon className="w-5 h-5" />
            </span>
            <p className="font-semibold text-gray-900">{t("cal.emptyTitle")}</p>
            <p className="text-sm text-gray-600 mt-1 max-w-md mx-auto">{t("cal.emptyBody")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {view === "today" && <TodayView events={events} onOpen={setSelected} t={t} locale={lang} />}
            {view === "week" && (
              <WeekView
                events={events}
                onOpen={setSelected}
                t={t}
                locale={lang}
                anchorDate={anchorDate}
                onAnchorChange={setAnchorDate}
              />
            )}
            {view === "month" && (
              <MonthView
                events={events}
                onOpen={setSelected}
                t={t}
                locale={lang}
                anchorDate={anchorDate}
                onAnchorChange={setAnchorDate}
              />
            )}
          </div>
        )}
      </main>

      <EventDetailsDialog
        event={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onUpdated={onUpdated}
        t={t}
        locale={lang}
      />

      <Footer />
    </div>
  );
}
